/**
 * persistence/vcs/BranchManager.js
 *
 * Owns all Dexie I/O for the git-style VCS layer: branches + commits.
 *
 * Model
 * ─────
 *   branch  — one analyst's working copy of a match.  Holds the live HEAD
 *             Yjs state (Uint8Array) and a pointer to its latest commit.
 *   commit  — an immutable Yjs snapshot + git metadata (message, parent,
 *             author, diff summary).  History forms a DAG; merge commits store
 *             two parents joined by "+".
 *
 * The default branch (`me/{analystId}`) is created lazily on the first edit or
 * the first history-screen open.  When it is created we SEED it from the
 * existing checkpoint history (the `versions` table) so no history is lost and
 * the commit log immediately shows the analyst's prior work.
 *
 * StringBridge holds the *live* in-memory doc; BranchManager reads it (via
 * getLiveDoc) when committing so a commit always captures the very latest
 * working state, then persists everything to Dexie.
 */

import { branchesTable, commitsTable } from "../db/database";
import {
  VCS_DEFAULT_BRANCH_PREFIX,
  VCS_BRANCH_COMMITS_MAX,
} from "../config";
import { takeChangeSources } from "../guard/stringGuard";
import { getClientInfo } from "../clientInfo";
import { getVersions } from "../managers/CheckpointManager";
import { countEvents, computeDiff } from "../managers/DiffManager";
import {
  loadDoc,
  encodeDoc,
  createDoc,
  setDocTokens,
  mergeStateInto,
  sharesHistory,
  docToString,
  stringToTokens,
} from "./yjsDoc";

// ─── Naming ─────────────────────────────────────────────────────────────────

/** The default (own) branch name for an analyst, e.g. "me/123". */
export function defaultBranchName(analystId) {
  return `${VCS_DEFAULT_BRANCH_PREFIX}/${analystId}`;
}

/** Generates a stable, collision-resistant commit id. */
export function genCommitId() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

// ─── Branch CRUD ──────────────────────────────────────────────────────────────

/**
 * Returns the analyst's default branch, creating and seeding it on first use.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<object>} the branch row
 */
export async function getOrCreateDefaultBranch(matchId, analystId) {
  const branchName = defaultBranchName(analystId);
  const existing = await getBranch(matchId, analystId, branchName);
  if (existing) return existing;
  return _createAndSeedDefaultBranch(matchId, analystId, branchName);
}

/**
 * Fetches a branch row by name, or null.
 */
export async function getBranch(matchId, analystId, branchName) {
  try {
    return (
      (await branchesTable()
        .where("[matchId+analystId+branchName]")
        .equals([matchId, analystId, branchName])
        .first()) || null
    );
  } catch (err) {
    console.warn("[BranchManager] getBranch failed:", err.message);
    return null;
  }
}

/**
 * Lists all branches for a session, most-recently-updated first.
 */
export async function listBranches(matchId, analystId) {
  try {
    const rows = await branchesTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .sortBy("updatedAt");
    return rows.reverse();
  } catch (err) {
    console.warn("[BranchManager] listBranches failed:", err.message);
    return [];
  }
}

/**
 * Upserts a branch's HEAD Yjs state.  Called (debounced) by StringBridge as
 * the analyst tags, and directly after a merge/checkout.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.branchName
 * @param {Uint8Array} params.stateUpdate
 * @param {string=} params.headCommitId  optional new HEAD pointer
 */
export async function updateBranchHead({
  matchId,
  analystId,
  branchName,
  stateUpdate,
  headCommitId,
}) {
  try {
    const existing = await getBranch(matchId, analystId, branchName);
    const patch = {
      stateUpdate,
      updatedAt: Date.now(),
      ...(headCommitId !== undefined ? { headCommitId } : {}),
    };
    if (existing) {
      await branchesTable().update(existing.id, patch);
    } else {
      await branchesTable().add({
        matchId,
        analystId,
        branchName,
        baseBranchName: null,
        headCommitId: headCommitId ?? null,
        stateUpdate,
        updatedAt: Date.now(),
      });
    }
  } catch (err) {
    console.warn("[BranchManager] updateBranchHead failed:", err.message);
  }
}

/**
 * Creates a new branch row from an explicit Yjs state (used by Pull and by
 * "fork from version").  No-ops if the branch already exists.
 *
 * @returns {Promise<object|null>} the branch row
 */
export async function createBranch({
  matchId,
  analystId,
  branchName,
  baseBranchName = null,
  stateUpdate,
  headCommitId = null,
}) {
  try {
    const existing = await getBranch(matchId, analystId, branchName);
    if (existing) return existing;
    const id = await branchesTable().add({
      matchId,
      analystId,
      branchName,
      baseBranchName,
      headCommitId,
      stateUpdate,
      updatedAt: Date.now(),
    });
    return await branchesTable().get(id);
  } catch (err) {
    console.warn("[BranchManager] createBranch failed:", err.message);
    return null;
  }
}

// ─── Commits ────────────────────────────────────────────────────────────────

/**
 * Creates a commit on a branch, snapshotting the current working state.
 *
 * The snapshot source is, in order of preference:
 *   1. an explicit `doc` passed in (e.g. a merge result),
 *   2. the live in-memory doc from StringBridge (latest keystrokes), else
 *   3. the branch's persisted HEAD state.
 *
 * @param {object} params
 * @param {string}  params.matchId
 * @param {string}  params.analystId
 * @param {string=} params.branchName        defaults to the analyst's branch
 * @param {string}  params.message
 * @param {string=} params.authorAnalystId   defaults to analystId
 * @param {import('yjs').Doc=} params.doc     explicit doc to snapshot
 * @param {string=} params.parentCommitId    override parent (merge commits)
 * @param {string=} params.triggerType        what created it (time/manual/exit/…)
 * @param {number=} params.createdAt          override timestamp (defaults to now)
 * @returns {Promise<object|null>} the commit row
 */
export async function commit({
  matchId,
  analystId,
  branchName,
  message,
  authorAnalystId,
  doc,
  parentCommitId,
  triggerType,
  createdAt,
}) {
  try {
    const name = branchName ?? defaultBranchName(analystId);
    let branch = await getBranch(matchId, analystId, name);
    if (!branch) branch = await getOrCreateDefaultBranch(matchId, analystId);

    // Resolve the doc to snapshot.
    let snapDoc = doc;
    if (!snapDoc) {
      const { getLiveDoc } = await import("./StringBridge");
      snapDoc = getLiveDoc(matchId, analystId) ?? loadDoc(branch.stateUpdate);
    }

    const stateUpdate = encodeDoc(snapDoc);
    const newString = docToString(snapDoc);
    const eventCount = countEvents(newString);

    // Diff vs the parent commit's string for a human-readable summary.
    const parent = parentCommitId !== undefined
      ? parentCommitId
      : branch.headCommitId ?? null;
    const parentString = await _commitString(parent);
    const diff = computeDiff(parentString, newString);

    // No-op guard: identical to HEAD → reuse the existing commit, write nothing.
    if (parent && diff.addedEvents.length === 0 && diff.removedEvents.length === 0) {
      return await getCommit(parent);
    }

    const diffSummary = {
      added: diff.addedEvents.length,
      removed: diff.removedEvents.length,
    };

    // Stable, ever-increasing display number (v1, v2, …).  Derived from the max
    // among existing commits so it survives pruning (the highest number is the
    // newest commit, which is never pruned) and stays consistent across paths
    // (auto checkpoint, manual save, merge) and devices.
    const existingCommits = await commitsTable()
      .where("[matchId+analystId+branchName]")
      .equals([matchId, analystId, name])
      .toArray();
    const nextVersionNumber =
      existingCommits.reduce((m, c) => Math.max(m, c.versionNumber ?? 0), 0) + 1;

    const commitId = genCommitId();
    await commitsTable().add({
      commitId,
      branchId: branch.id,
      matchId,
      analystId,
      branchName: name,
      parentCommitId: parent,
      message: message ?? "Checkpoint",
      authorAnalystId: authorAnalystId ?? analystId,
      triggerType: triggerType ?? "manual",
      versionNumber: nextVersionNumber,
      stateUpdate,
      eventCount,
      diffSummary,
      sources: takeChangeSources(matchId, analystId),
      client: getClientInfo(),
      createdAt: createdAt ?? Date.now(),
      pushed: false,
    });

    // Advance the branch HEAD to this commit and store its state.
    await updateBranchHead({
      matchId,
      analystId,
      branchName: name,
      stateUpdate,
      headCommitId: commitId,
    });

    await pruneCommits(matchId, analystId, name);
    return await getCommit(commitId);
  } catch (err) {
    console.warn("[BranchManager] commit failed:", err.message);
    return null;
  }
}

/**
 * Creates a commit from an explicit event string — the entry point used by the
 * checkpoint funnel (CheckpointManager._createCheckpoint) so every checkpoint
 * trigger produces a commit.
 *
 * Reconciles `currentString` into the analyst's working branch doc (preferring
 * the live in-memory doc when the analyst is actively editing, else the
 * persisted HEAD — works sessionlessly for TechQC) and commits the result.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.currentString
 * @param {string} params.message
 * @param {string=} params.triggerType
 * @param {number=} params.createdAt
 * @returns {Promise<object|null>} the commit row
 */
export async function commitFromString({
  matchId,
  analystId,
  currentString,
  message,
  triggerType,
  createdAt,
}) {
  try {
    const name = defaultBranchName(analystId);
    const branch = await getOrCreateDefaultBranch(matchId, analystId);

    // Prefer the live doc (active analyst) so we capture exactly what's being
    // edited; fall back to the persisted HEAD for sessionless callers (TechQC).
    let doc = null;
    try {
      const { getLiveDoc } = await import("./StringBridge");
      doc = getLiveDoc(matchId, analystId);
    } catch {
      doc = null;
    }
    if (!doc) doc = loadDoc(branch.stateUpdate);

    setDocTokens(doc, stringToTokens(currentString ?? ""));

    return commit({
      matchId,
      analystId,
      branchName: name,
      message,
      triggerType,
      createdAt,
      doc,
    });
  } catch (err) {
    console.warn("[BranchManager] commitFromString failed:", err.message);
    return null;
  }
}

/**
 * Lists a branch's commits, newest-first.
 */
export async function getCommits(matchId, analystId, branchName) {
  try {
    const name = branchName ?? defaultBranchName(analystId);
    const rows = await commitsTable()
      .where("[matchId+analystId+branchName]")
      .equals([matchId, analystId, name])
      .sortBy("createdAt");
    return rows.reverse();
  } catch (err) {
    console.warn("[BranchManager] getCommits failed:", err.message);
    return [];
  }
}

/**
 * Fetches a single commit by commitId, or null.
 */
export async function getCommit(commitId) {
  if (!commitId) return null;
  try {
    return (await commitsTable().where("commitId").equals(commitId).first()) || null;
  } catch (err) {
    console.warn("[BranchManager] getCommit failed:", err.message);
    return null;
  }
}

/**
 * Returns the plain event string for a commit (for previewing / checkout).
 *
 * @param {string} commitId
 * @returns {Promise<string>}
 */
export async function checkout(commitId) {
  return _commitString(commitId);
}

/**
 * Prunes a branch's oldest commits beyond VCS_BRANCH_COMMITS_MAX.
 * The branch HEAD state lives in the branches table, so pruning old commits
 * never loses current work — only deep history.
 */
export async function pruneCommits(matchId, analystId, branchName) {
  try {
    const name = branchName ?? defaultBranchName(analystId);
    const all = await commitsTable()
      .where("[matchId+analystId+branchName]")
      .equals([matchId, analystId, name])
      .sortBy("createdAt"); // oldest-first
    const excess = all.length - VCS_BRANCH_COMMITS_MAX;
    if (excess > 0) {
      await commitsTable().bulkDelete(all.slice(0, excess).map((c) => c.id));
    }
  } catch (err) {
    console.warn("[BranchManager] pruneCommits failed:", err.message);
  }
}

// ─── Cross-device sync ──────────────────────────────────────────────────────

/**
 * Re-hydrates the analyst's OWN branch from Firestore on any device.
 *
 * Folds the cloud branch HEAD into the local working branch (CRDT merge —
 * conflict-free and idempotent, so this is safe to call repeatedly and on a
 * device that already has local edits) and imports any cloud commits not yet
 * present locally, so the commit log matches across devices.
 *
 * Pairs with the manual "Share" (pushBranch): one device shares, others fetch.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<{fetched: boolean, newCommits: number}>}
 */
export async function syncOwnBranchFromCloud({ matchId, analystId }) {
  try {
    const { fetchOwnBranch } = await import("./vcsFirestore"); // lazy: avoid cycle
    const remote = await fetchOwnBranch({ matchId, analystId });
    if (!remote) return { fetched: false, newCommits: 0 };

    const branchName = defaultBranchName(analystId);
    const local = await getOrCreateDefaultBranch(matchId, analystId);

    // Merge cloud HEAD into the local doc — only when both come from the same
    // history.  Unrelated states (e.g. each device seeded separately) would be
    // concatenated, duplicating events; keep the local HEAD in that case.
    const doc = loadDoc(local.stateUpdate);
    const localHasEvents = countEvents(docToString(doc)) > 0;
    const canMerge = !localHasEvents || sharesHistory(local.stateUpdate, remote.stateUpdate);
    if (canMerge) mergeStateInto(doc, remote.stateUpdate);
    const mergedState = encodeDoc(doc);

    // Import commits we don't already have (dedupe by commitId).
    const existing = await getCommits(matchId, analystId, branchName);
    const haveIds = new Set(existing.map((c) => c.commitId));
    let newCommits = 0;
    for (const c of remote.commits) {
      if (haveIds.has(c.commitId)) continue;
      await commitsTable().add({
        commitId: c.commitId,
        branchId: local.id,
        matchId,
        analystId,
        branchName,
        parentCommitId: c.parentCommitId,
        message: c.message,
        authorAnalystId: c.authorAnalystId,
        triggerType: c.triggerType,
        versionNumber: c.versionNumber,
        stateUpdate: c.stateUpdate,
        eventCount: c.eventCount,
        diffSummary: c.diffSummary,
        sources: c.sources,
        client: c.client,
        headConflict: c.headConflict,
        createdAt: c.createdAt,
        pushed: true, // came from the cloud — already shared
      });
      newCommits++;
    }

    // Persist the merged HEAD (and adopt the cloud HEAD pointer if we have it).
    await updateBranchHead({
      matchId,
      analystId,
      branchName,
      stateUpdate: mergedState,
      headCommitId: canMerge ? remote.headCommitId ?? local.headCommitId : local.headCommitId,
    });

    await pruneCommits(matchId, analystId, branchName);
    return { fetched: true, newCommits };
  } catch (err) {
    console.warn("[BranchManager] syncOwnBranchFromCloud failed:", err.message);
    return { fetched: false, newCommits: 0 };
  }
}

// ─── Internal ─────────────────────────────────────────────────────────────────

/**
 * Decodes a commit's snapshot to its event string. Empty string if missing.
 */
async function _commitString(commitId) {
  if (!commitId) return "";
  // Merge commits store "parentA+parentB"; for diffing we only need the first
  // parent's lineage string, which is what computeDiff compares against.
  const realId = String(commitId).split("+")[0];
  const c = await getCommit(realId);
  if (!c) return "";
  return docToString(loadDoc(c.stateUpdate));
}

/**
 * Creates the default branch and seeds its commit history from the existing
 * checkpoint snapshots (the `versions` table), so prior work appears in the
 * git log on first open.
 *
 * Builds ONE doc incrementally — each version becomes a CRDT descendant of the
 * previous — so the seeded history is a real DAG that merges/diffs correctly.
 */
async function _createAndSeedDefaultBranch(matchId, analystId, branchName) {
  const doc = createDoc();
  let headCommitId = null;
  let prevString = "";

  try {
    const versions = await getVersions(matchId, analystId); // oldest-first
    for (const v of versions) {
      const tokens = stringToTokens(v.string ?? "");
      setDocTokens(doc, tokens);
      const newString = docToString(doc);
      const diff = computeDiff(prevString, newString);
      const commitId = genCommitId();
      // Insert the branch row lazily on the first commit so branchId is known.
      const branch = await _ensureBranchRow(matchId, analystId, branchName, doc);
      await commitsTable().add({
        commitId,
        branchId: branch.id,
        matchId,
        analystId,
        branchName,
        parentCommitId: headCommitId,
        message: _seedMessage(v),
        authorAnalystId: analystId,
        triggerType: v.triggerType ?? "unknown",
        versionNumber: v.versionNumber ?? null,
        stateUpdate: encodeDoc(doc),
        eventCount: countEvents(newString),
        diffSummary: { added: diff.addedEvents.length, removed: diff.removedEvents.length },
        createdAt: v.createdAt ?? Date.now(),
        pushed: Boolean(v.synced), // already-synced versions are effectively pushed
      });
      headCommitId = commitId;
      prevString = newString;
    }
  } catch (err) {
    console.warn("[BranchManager] seeding from versions failed:", err.message);
  }

  // Ensure the branch row exists even when there were no versions to seed.
  const branch = await _ensureBranchRow(matchId, analystId, branchName, doc);
  if (headCommitId && branch.headCommitId !== headCommitId) {
    await branchesTable().update(branch.id, {
      headCommitId,
      stateUpdate: encodeDoc(doc),
      updatedAt: Date.now(),
    });
  }
  return (await getBranch(matchId, analystId, branchName)) ?? branch;
}

/** Upserts the branch row and returns it (used during seeding). */
async function _ensureBranchRow(matchId, analystId, branchName, doc) {
  const existing = await getBranch(matchId, analystId, branchName);
  if (existing) return existing;
  const id = await branchesTable().add({
    matchId,
    analystId,
    branchName,
    baseBranchName: null,
    headCommitId: null,
    stateUpdate: encodeDoc(doc),
    updatedAt: Date.now(),
  });
  return await branchesTable().get(id);
}

/** Human-friendly seed-commit message derived from a checkpoint's trigger. */
function _seedMessage(version) {
  const v = version.versionNumber ? `v${version.versionNumber} · ` : "";
  return `${v}${version.triggerType ?? "checkpoint"}`;
}
